import React, { useContext, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, Bug } from "lucide-react";
import { toast } from "sonner";
import AuthContext from "@/store/contexts/AuthContext";
import { osolutionClient as axios } from "@/services/axios";
import { t } from "i18next";

const mailto = (subject: string, body: string) => {
  const link = document.createElement("a");
  link.href = `mailto:support@odashboard.app?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ReportBugButton: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [description, setDescription] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [includeConfig, setIncludeConfig] = React.useState(true);
  const [loading, setLoading] = useState(false);
  const {
    user,
    isEditor,
    isPdf,
    pages,
    configs,
    models,
    editMode,
    osolutionAuthData,
  } = useContext(AuthContext);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Collect comprehensive browser data
    const browserData = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      languages: navigator.languages,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      localStorage: {} as any,
      sessionStorage: {} as any,
    };

    // Collect localStorage data (safely)
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          try {
            const value = localStorage.getItem(key);
            browserData.localStorage[key] = value ? JSON.parse(value) : value;
          } catch {
            browserData.localStorage[key] = localStorage.getItem(key);
          }
        }
      }
    } catch (error) {
      browserData.localStorage = { error: "Could not access localStorage" };
    }

    // Collect sessionStorage data (safely)
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          try {
            const value = sessionStorage.getItem(key);
            browserData.sessionStorage[key] = value ? JSON.parse(value) : value;
          } catch {
            browserData.sessionStorage[key] = sessionStorage.getItem(key);
          }
        }
      }
    } catch (error) {
      browserData.sessionStorage = { error: "Could not access sessionStorage" };
    }

    // Collect application data if enabled
    let appData = {};
    if (includeConfig && user) {
      appData = {
        user: {
          plan: user.plan,
          // Don't include sensitive user data
        },
        isEditor,
        isPdf,
        editMode,
        pagesCount: pages?.length || 0,
        configsCount: configs?.length || 0,
        modelsCount: models?.length || 0,
        osolutionAuth: {
          hasUrl: !!osolutionAuthData?.current?.url,
          hasToken: !!osolutionAuthData?.current?.token,
          // Don't include actual credentials
        },
        pages:
          pages?.map((page) => ({
            id: page.id,
            name: page.title,
            contentCount: page.content?.length || 0,
            // Don't include actual content
          })) || [],
        configs:
          configs?.map((config) => ({
            id: config.id,
            name: config.title,
            type: config.type,
            // Don't include sensitive config data
          })) || [],
      };
    }

    const reportData = {
      browser: browserData,
      ...(includeConfig && { application: appData }),
    };

    await axios
      .post(
        `${import.meta.env.VITE_OSOLUTION_API}/api/helpdesk/create_ticket`,
        {
          description: description,
          description_json: reportData,
          email: email || undefined,
        }
      )
      .then(() => {
        toast.success(
          t("reportBugButton.success")
        );
      })
      .catch(() => {
        toast.error(t("reportBugButton.error"));
      });

    setOpen(false);
    setDescription("");
    setEmail("");
    setLoading(false);
  };

  return (
    <>
      {/* Floating trigger */}
      <div className="fixed right-4 bottom-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-3 rounded-full shadow-md hover:shadow-lg"
          aria-label={t("home.reportBugButton.buttonLabel")}
          title={t("home.reportBugButton.buttonLabel")}
        >
          <Bug className="h-4 w-4" />
          <span>{t("home.reportBugButton.buttonLabel")}</span>
        </Button>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t("home.reportBugButton.dialogTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("home.reportBugButton.dialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bug-description">{t("home.reportBugButton.descriptionLabel")}</Label>
              <Textarea
                id="bug-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("home.reportBugButton.descriptionPlaceholder")}
                className="min-h-32"
                autoFocus
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-email">
                {t("home.reportBugButton.emailLabel")} <span className="text-muted-foreground text-xs">({t("home.reportBugButton.optional")})</span>
              </Label>
              <Input
                id="bug-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("home.reportBugButton.emailPlaceholder")}
              />
              <p className="text-xs text-muted-foreground">
                {t("home.reportBugButton.emailDescription")}
              </p>
            </div>

            <div className="flex items-center justify-between space-x-2">
              <div className="space-y-1">
                <Label htmlFor="include-config" className="text-sm font-medium">
                  {t("home.reportBugButton.includeConfigLabel")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("home.reportBugButton.includeConfigDescription")}
                </p>
              </div>
              <Switch
                id="include-config"
                checked={includeConfig}
                onCheckedChange={setIncludeConfig}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={loading}
                onClick={() => setOpen(false)}
              >
                {t("home.reportBugButton.cancel")}
              </Button>
              <Button disabled={loading} type="submit">
                {t("home.reportBugButton.sendReport")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportBugButton;
